import 'twin.macro'

import { Ring } from '@uiball/loaders'
import Typography from 'components/UI/Typography'
import Heading from 'components/UI/Typography/Heading'
import dayjs from 'dayjs'
import durationPlugin from 'dayjs/plugin/duration'
import { useAppSelector } from 'hooks'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApplicationStatusQuery } from 'redux/api'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import tw from 'twin.macro'

import RetryTimer from './RetryTimer'

dayjs.extend(durationPlugin)

const RetryStepPage = () => {
  // Tools
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const currentFlowId = useAppSelector(getCurrentFlowId)

  // Queries
  const { data, isLoading, isSuccess } = useGetApplicationStatusQuery(currentFlowId as string, {
    skip: !currentFlowId
  })

  // Computed values
  const isColvirError = useMemo(() => {
    if (!isSuccess) return
    const { current_step, next_step_depends_on_colvir_worktime, is_colvir_worktime } = data
    if (!current_step) return
    return !is_colvir_worktime && (current_step.depends_on_colvir_worktime || next_step_depends_on_colvir_worktime)
  }, [data, isSuccess])

  const expiryTimestamp = useMemo(() => {
    if (!isSuccess) return new Date()
    const { current_step, colvir_available_at } = data
    if (isColvirError) return new Date(colvir_available_at)
    if (!current_step) return new Date()
    return dayjs().add(current_step.retry_at, 's').toDate()
  }, [data, isColvirError, isSuccess])

  if (!isSuccess || isLoading) {
    return (
      <div tw='flex justify-center items-center h-[calc(100vh - 58px)] md:h-[calc(100vh - 84px)] max-w-xl mx-auto'>
        <div tw='bg-secondary flex flex-col items-center justify-center rounded-md w-72 h-72'>
          <Ring color='var(--primary-color)' />
        </div>
      </div>
    )
  }

  return (
    <div tw='flex justify-center items-center h-[calc(100vh - 58px)] md:h-[calc(100vh - 84px)] max-w-xl mx-auto'>
      <div tw='bg-secondary flex flex-col items-center pt-9 md:pt-24 pb-9 px-9 md:px-20 rounded-md'>
        <Heading variant='subtitle' weight='bold' twStyle={tw`text-center`}>
          {isColvirError ? t('unable-to-continue') : t('flow-sorry')}
        </Heading>
        <Typography variant='subbody' twStyle={tw`text-grey-dark mt-3 mb-16 text-center`}>
          {isColvirError
            ? null
            : language === 'ru'
            ? data.current_step?.status_reason
            : data.current_step?.status_reason_kk}
        </Typography>
        <Typography variant='subbody'>{isColvirError ? t('retry-in') : t('flow-can-retry')}</Typography>
        <RetryTimer timestamp={expiryTimestamp} />
      </div>
    </div>
  )
}

export default RetryStepPage
